/**
 * /docs/onboarding — the public onboarding guide, in the Carbon type of
 * mockup carbon/01 inside the 04 public frame. The step headings for the
 * setup guide ("3.1 Connect" … "3.5 Watch") stay pinned to the wizard's own
 * copy (`JOURNEY_STEP_COPY`, the render test). Neutral throughout: notes and
 * confirmations carry an info or check glyph, never a hue.
 */
import { Link } from 'react-router-dom'
import { Accordion, ButtonLink, CodeSnippet, CondensedGrid, IconArrowRight, Tile, TileHeader } from '@/components/carbon'
import { PUBLIC_LINK } from '@/components/PublicLayout'
import { CheckList, DocList, DocNote, DocsHeader, DocSection, DocsPage, DocSubsection, Strong } from './docs-ui'

function Command({ code }: { code: string }) {
  return <CodeSnippet kind="single" code={code} language="PowerShell" copyLabel="Copy command" />
}

function Code({ children }: { children: string }) {
  return <CodeSnippet kind="inline" code={children} />
}

const PHASES = [
  {
    title: 'Sign up and consent',
    body: 'Sign up through our self-service portal and grant admin consent on Microsoft’s own consent screen.',
  },
  {
    title: 'We approve your organization',
    body: 'A member of the Guardian team reviews and approves your organization, usually within a few hours.',
  },
  {
    title: 'Connect your tenant',
    body: 'A short guided setup: run one script, paste back the result, size your coverage, and switch on watching.',
  },
]

const CHECKLIST = [
  'Signed up at guardian.iuvotech.com/signup',
  'Accepted permissions on the Microsoft consent screen',
  'Organization approved by the Guardian team',
  'Signed in and reached the setup guide',
  'Downloaded the setup script and ran it as Global Administrator',
  'Pasted the script’s output back and verified the connection',
  'Sized coverage with a full configuration capture',
  'Chose the coverage to watch',
  'Switched on continuous watch',
]

export default function OnboardingGuide() {
  return (
    <DocsPage crumbs={[{ label: 'Documentation', to: '/docs' }, { label: 'Onboarding guide' }]}>
      <DocsHeader
        title="Sign up, get approved, then connect your tenant."
        lede="One setup script from your Microsoft 365 admin connects your tenant. From there, Guardian reads your configuration and starts watching for risky changes."
        meta={
          <>
            <span>Your part: about 10 minutes</span>
            <span>Requires: Global Administrator</span>
          </>
        }
      />

      {/* Overview */}
      <DocSection title="How it works" id="how-it-works">
        <p className="m-0">
          Three phases get you from sign-up to a live connection: consent, approval, then a short guided setup. The
          setup itself is five steps, and most of them are ours.
        </p>
        <CondensedGrid as="ol" columns={{ base: 1, 'c-md': 3 }}>
          {PHASES.map((phase, i) => (
            <Tile as="li" key={phase.title} className="flex min-w-0 flex-col gap-2">
              <p className="m-0 text-helper text-text-helper">Phase {i + 1}</p>
              <TileHeader title={phase.title} level={3} />
              <p className="m-0 text-body text-text-secondary">{phase.body}</p>
            </Tile>
          ))}
        </CondensedGrid>
      </DocSection>

      {/* Step 1: Sign up and consent */}
      <DocSection title="Step 1: Sign up and grant consent" id="step-1">
        <DocSubsection title="1.1 Start the sign-up process">
          <p className="m-0">Visit the Guardian sign-up page and enter your email, name, and company:</p>
          <Tile>
            <a href="https://guardian.iuvotech.com/signup" className={PUBLIC_LINK}>
              https://guardian.iuvotech.com/signup
            </a>
          </Tile>
          <p className="m-0">
            Submitting the form takes you straight to Microsoft&rsquo;s admin consent screen. Your setup script comes
            later, once your organization is approved and you&rsquo;re signed in to Guardian.
          </p>
        </DocSubsection>

        <DocSubsection title="1.2 Sign in as Global Administrator">
          <p className="m-0">Microsoft redirects you to its own sign-in page:</p>
          <DocList
            ordered
            items={[
              <>Sign in with your <Strong>Global Administrator</Strong> account</>,
              'Complete MFA if prompted',
              'You’ll see the Microsoft permissions consent screen',
            ]}
          />
        </DocSubsection>

        <DocSubsection title="1.3 Review and accept permissions">
          <p className="m-0">
            The consent screen shows the permissions Guardian is requesting. Most are read-only and a few are
            read-write. Guardian uses write access only for its own Tenant Configuration Management objects in your
            tenant: it creates, updates and deletes its configuration monitors, and creates configuration snapshots. It
            doesn&rsquo;t change your policies or settings.
          </p>
          <DocNote title="Guardian reads your configuration, including:">
            <DocList
              items={[
                'Directory and organization settings',
                'Conditional Access policies',
                'Security and compliance configurations',
                'Exchange Online settings',
                'SharePoint and Teams configurations',
                'Intune device management policies',
                'Directory details about your users, such as names, sign-in activity, licences and MFA status',
              ]}
            />
          </DocNote>
          <p className="m-0">
            Click <Strong>&ldquo;Accept&rdquo;</Strong> to grant consent for your organization. See{' '}
            <Link to="/docs/permissions" className={PUBLIC_LINK}>
              Permissions &amp; security
            </Link>{' '}
            for the full permission list and why each one is requested.
          </p>
          <DocNote tone="check">
            <p className="m-0">
              After accepting, you&rsquo;ll land on a success page. Step 1 is complete, and your organization moves to
              review.
            </p>
          </DocNote>
        </DocSubsection>
      </DocSection>

      {/* Step 2: Approval */}
      <DocSection title="Step 2: We approve your organization" id="step-2">
        <p className="m-0">
          Before Guardian starts monitoring, a member of the Guardian team reviews and approves your organization. This
          is a human review, not an automated check, but it&rsquo;s usually done within a few hours, not days.
        </p>
        <p className="m-0">
          If you sign in to Guardian while your organization is still waiting on approval, you&rsquo;ll land on a
          waiting screen that checks in the background and forwards you into the app the moment
          you&rsquo;re approved. There&rsquo;s nothing to click, and no need to keep refreshing.
        </p>
      </DocSection>

      {/* Step 3: Connect your tenant */}
      <DocSection title="Step 3: Connect your tenant" id="step-3">
        <p className="m-0">
          Once you&rsquo;re approved and signed in, Guardian takes you straight to your setup guide. It walks through
          five steps: Connect, Verify, Size, Choose scope, and Watch. Each step&rsquo;s action becomes available in turn,
          right there on the page.
        </p>

        <DocNote title="Before you start, your Microsoft 365 admin needs:">
          <DocList
            items={[
              <><Strong>PowerShell 7.0 or newer.</Strong> The setup script won&rsquo;t run on Windows PowerShell 5.1.</>,
              <>
                <Strong>Three PowerShell modules:</Strong> <Code>Microsoft.Graph.Authentication</Code>,{' '}
                <Code>Microsoft.Graph.Applications</Code>, and <Code>ExchangeOnlineManagement</Code> (3.4.0 or newer).
              </>,
              <>
                <Strong>Global Administrator</Strong> in the tenant.
              </>,
              <>
                <Strong>A qualifying Microsoft plan:</Strong> Microsoft Entra ID P1 or P2, on its own or in a plan that
                includes it, such as Microsoft&nbsp;365 E3 or E5 or Business Premium. Microsoft&rsquo;s configuration
                monitoring requires it. GCC High and DoD tenants aren&rsquo;t supported, and GCC tenants haven&rsquo;t
                been tested yet.
              </>,
              <>
                Up to <Strong>two browser sign-ins</Strong> while the script runs, once for Microsoft Graph and once for
                Exchange Online.
              </>,
            ]}
          />
        </DocNote>

        <DocSubsection title="3.1 Connect">
          <p className="m-0">
            Click <Strong>Get the setup script</Strong>. Guardian builds a setup script tailored to your tenant and
            downloads it. It&rsquo;s good for 24 hours, and your Microsoft 365 admin runs it in about ten minutes. It
            sets up the enterprise apps Guardian needs in your tenant and grants them the roles and permissions in the{' '}
            <Link to="/docs/permissions" className={PUBLIC_LINK}>
              permissions guide
            </Link>
            , most of them read-only. Apart from turning on Exchange organization customization if it&rsquo;s off, and
            removing the Compliance Administrator role from Microsoft&rsquo;s M365 Tenant Configuration Manager app unless
            your setup includes Purview compliance monitoring, the script doesn&rsquo;t change your policies or settings.
          </p>
          <p className="m-0">
            If you aren&rsquo;t the Microsoft 365 admin yourself, the setup guide has a{' '}
            <Strong>&ldquo;Not the Microsoft 365 admin? Send it to them&rdquo;</Strong> link that prepares an email with
            the script and instructions attached, ready to forward.
          </p>
          <p className="m-0">
            In a PowerShell 7 window, go to the folder the script was saved to. If it was downloaded from Guardian,
            that&rsquo;s usually Downloads:
          </p>
          <Command code="cd $HOME\Downloads" />
          <p className="m-0">Windows blocks scripts downloaded from the web until you unblock them, so unblock the file next:</p>
          <Command code={'Unblock-File .\\Guardian-Onboarding-*.ps1'} />
          <p className="m-0 text-helper text-text-helper">This clears the download block on that one file.</p>
          <p className="m-0">
            Run the script by name from the same PowerShell 7 window. (Avoid right-click &rarr; Run with PowerShell: on
            most Windows systems that opens the older Windows PowerShell 5.1, which this script declines to run
            under.)
          </p>
          <Command code={'.\\Guardian-Onboarding-*.ps1'} />
          <p className="m-0">If prompted about execution policy, this also may be needed:</p>
          <Command code="Set-ExecutionPolicy -Scope Process -ExecutionPolicy Bypass" />
          <p className="m-0 text-helper text-text-helper">
            This affects only the current PowerShell session. You do not need to run PowerShell as Administrator.
          </p>
          <p className="m-0">
            The script signs in twice: once for Microsoft Graph, once for Exchange Online. Complete MFA if prompted for
            either.
          </p>
          <DocNote title="A first run can ask for a second one.">
            <p className="m-0">
              If your Exchange Online organization has never been customized, the script turns on organization
              customization, a one-time Microsoft setting that Exchange role assignments need. It finishes the other
              grants and asks you to re-run it in 15-30 minutes, once the change has propagated, to finish the Exchange
              roles. This only happens once.
            </p>
          </DocNote>
          <p className="m-0">
            When it finishes, the script prints a block of text ending in a JSON payload. Copy that whole block, start
            to finish.
          </p>
        </DocSubsection>

        <DocSubsection title="3.2 Verify">
          <p className="m-0">
            Paste the output the script printed into the box on the setup guide and click{' '}
            <Strong>Verify the connection</Strong>. Guardian checks the connection end to end and confirms every
            permission landed.
          </p>
          <p className="m-0">
            If Microsoft is still propagating the permissions it just granted, Guardian re-checks the same pasted output
            on its own every 45 seconds or so, for up to a few minutes, before asking you to do anything. If the check
            genuinely fails, ask your Microsoft 365 admin for a fresh script (each one is good for 24 hours) and paste
            the new output.
          </p>
        </DocSubsection>

        <DocSubsection title="3.3 Size">
          <p className="m-0">
            Click <Strong>Size my coverage</Strong>. Guardian takes a full capture of your configuration so the coverage
            options that follow reflect what actually exists in your tenant, not an estimate. This usually finishes
            within a few minutes, and the page keeps itself up to date while it runs.
          </p>
        </DocSubsection>

        <DocSubsection title="3.4 Choose scope">
          <p className="m-0">
            Once sized, Guardian starts you on Identity &amp; access and Threat protection, the coverage that fits every
            tenant we&rsquo;ve measured. There&rsquo;s nothing to decide: click through and continuous watch switches on
            with that coverage in place.
          </p>
          <p className="m-0">
            Want something different first? Click <Strong>Adjust coverage</Strong> to open the picker, add Endpoint
            &amp; device or Collaboration (Governance &amp; compliance appears if your organization has opted in), and
            see a live meter of how much of your selection continuous watch can follow closely, in real settings counted
            from your capture, of about 200 settings total. You can widen or narrow your coverage at any time later, from
            either path.
          </p>
        </DocSubsection>

        <DocSubsection title="3.5 Watch">
          <p className="m-0">
            Click <Strong>Switch on continuous watch</Strong>. Continuous monitoring goes live immediately. Changes to
            your highest-impact settings start landing in the queue, ranked and explained, usually within a few hours as
            the first assessments complete.
          </p>
          <DocNote tone="check">
            <p className="m-0">Setup is complete. Your dashboard fills in as the first assessments land.</p>
          </DocNote>
        </DocSubsection>
      </DocSection>

      {/* What Guardian monitors */}
      <DocSection title="What Guardian monitors" id="what-guardian-monitors">
        <CondensedGrid columns={{ base: 1, 'c-md': 2 }}>
          <Tile className="flex min-w-0 flex-col gap-3">
            <TileHeader title="What Guardian can see" level={3} />
            <DocList
              items={[
                <><Strong>Identity &amp; access:</Strong> who can get in, from where, and how strongly they must prove it</>,
                <><Strong>Threat protection:</Strong> mail flow, anti-spam, and data-loss controls</>,
                <><Strong>Endpoint &amp; device:</Strong> whether managed devices meet your security bar</>,
                <><Strong>Collaboration:</Strong> how open Teams is to the outside world</>,
                <><Strong>Governance &amp; compliance:</Strong> data protection and retention policy (opt-in)</>,
                <><Strong>SharePoint &amp; OneDrive:</Strong> site and sharing configuration, reviewed on every scan</>,
              ]}
            />
          </Tile>
          <Tile className="flex min-w-0 flex-col gap-3">
            <TileHeader title="What Guardian doesn’t do" level={3} />
            <DocList
              items={[
                'Change your policies or settings',
                'Read email, files or Teams chats',
                'Read passwords',
              ]}
            />
            <p className="m-0 text-body text-text-secondary">
              In your tenant, Guardian writes only its own Tenant Configuration Management objects: it creates, updates
              and deletes its configuration monitors, and creates configuration snapshots. Accepting a change updates
              Guardian&rsquo;s baseline, not your settings.
            </p>
          </Tile>
        </CondensedGrid>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting" id="troubleshooting">
        <Accordion
          defaultOpen={['consent-declined']}
          items={[
            {
              id: 'consent-declined',
              title: '“Consent was declined” or the consent screen doesn’t appear',
              content: (
                <div className="flex flex-col gap-3">
                  <p className="m-0">This usually means:</p>
                  <DocList
                    items={[
                      'You’re not signed in as a Global Administrator',
                      'Your organization has restricted third-party app consent',
                      'A browser popup was blocked',
                    ]}
                  />
                  <p className="m-0">
                    <Strong>Solution:</Strong> Try an InPrivate or Incognito browser window, or contact your IT
                    administrator if consent is restricted.
                  </p>
                </div>
              ),
            },
            {
              id: 'not-approved',
              title: 'My organization hasn’t been approved yet',
              content: (
                <p className="m-0">
                  Consent alone doesn&rsquo;t turn monitoring on. A member of the Guardian team reviews and approves your
                  organization first, usually within a few hours. If you sign in before that happens, Guardian shows a
                  waiting screen that keeps checking and forwards you the moment you&rsquo;re approved.
                </p>
              ),
            },
            {
              id: 'module-not-found',
              title: '“Module not found” (Microsoft.Graph or Exchange)',
              content: (
                <div className="flex flex-col gap-3">
                  <p className="m-0">Install the modules the script requires:</p>
                  <Command code="Install-Module Microsoft.Graph.Authentication, Microsoft.Graph.Applications, ExchangeOnlineManagement -Scope CurrentUser -Force" />
                  <p className="m-0">Then re-run the script.</p>
                </div>
              ),
            },
            {
              id: 'script-blocked',
              title: 'The script won’t run, or Windows blocks it',
              content: (
                <div className="flex flex-col gap-3">
                  <p className="m-0">
                    Windows marks anything downloaded from the web and blocks it from running. Unblock the file first:
                  </p>
                  <Command code={'Unblock-File .\\Guardian-Onboarding-*.ps1'} />
                  <p className="m-0">
                    If PowerShell also complains about execution policy, run{' '}
                    <Code>Set-ExecutionPolicy -Scope Process -ExecutionPolicy Bypass</Code> first. This affects only
                    that PowerShell session. Also confirm you&rsquo;re running PowerShell 7.0 or newer, not Windows
                    PowerShell 5.1.
                  </p>
                </div>
              ),
            },
            {
              id: 're-run',
              title: 'The script says “re-run in 15-30 minutes”',
              content: (
                <p className="m-0">
                  This means your Exchange Online organization had never been customized. The script turned on
                  organization customization and finished the other grants, but held back the Exchange role assignments
                  on purpose. Wait 15-30 minutes for the change to propagate through Microsoft&rsquo;s systems, then run
                  the same script again to finish them.
                </p>
              ),
            },
            {
              id: 'insufficient-privileges',
              title: '“Insufficient privileges”',
              content: (
                <p className="m-0">
                  You need the <Strong>Global Administrator</Strong> role to complete this setup. Contact your IT team if
                  you don&rsquo;t have it.
                </p>
              ),
            },
            {
              id: 'verify-failed',
              title: '“That check didn’t pass” during Verify',
              content: (
                <div className="flex flex-col gap-3">
                  <p className="m-0">
                    Nothing was changed in your tenant. This means the pasted output didn&rsquo;t match what Guardian
                    expected, or the script itself hit a problem. Try:
                  </p>
                  <DocList
                    ordered
                    items={[
                      'Make sure you copied the entire block the script printed, start to finish',
                      'Ask your Microsoft 365 admin to run a fresh script (each one is good for 24 hours)',
                      'Paste the new output and verify again',
                    ]}
                  />
                </div>
              ),
            },
          ]}
        />
      </DocSection>

      {/* Checklist */}
      <DocSection title="Setup checklist" id="checklist">
        <Tile>
          <CheckList items={CHECKLIST} />
        </Tile>
      </DocSection>

      {/* Revoking access */}
      <DocSection title="Revoking access" id="revoking-access">
        <p className="m-0">
          You can revoke Guardian&rsquo;s access at any time. To remove what setup added:
        </p>
        <DocList
          ordered
          items={[
            <>
              In <Strong>Microsoft Entra ID</Strong> &rarr; <Strong>Enterprise applications</Strong>, find Guardian&rsquo;s
              app by its application ID, <Code>10ab017c-a1da-422f-be0e-fda46c03bec9</Code>, and delete it. Guardian can
              no longer read your tenant.
            </>,
            <>
              In Exchange Online, remove the role assignments the setup script made (View-Only Recipients for
              Guardian&rsquo;s app; View-Only Configuration and E-Mail Address Policies for the M365 Tenant Configuration
              Manager app) and the service principals it registered in Exchange for those two apps.
            </>,
            <>
              Microsoft&rsquo;s <Strong>M365 Tenant Configuration Manager</Strong> enterprise app: delete it only if the
              setup script created it (its saved report,{' '}
              <Code>guardian-onboarding-report-&lt;domain&gt;-&lt;date&gt;.json</Code>, says <Strong>Created</Strong> for
              that app, not <Strong>Already present</Strong>) and you don&rsquo;t use
              Microsoft&rsquo;s Tenant Configuration Management yourself. Deleting it also removes the roles and
              permissions the script gave it, including <Code>Exchange.ManageAsApp</Code>. If you use Tenant
              Configuration Management, keep it; the{' '}
              <Link to="/docs/permissions" className={PUBLIC_LINK}>
                permissions guide
              </Link>{' '}
              lists the roles and permissions the script added. Keep Microsoft&rsquo;s{' '}
              <Strong>M365 Admin Services</Strong> app either way: the script gave it no permissions.
            </>,
            <>
              In <Strong>Enterprise applications</Strong>, open <Strong>Microsoft Graph Command Line Tools</Strong> and
              remove the permissions your admin granted when the script signed in, unless your admins use Microsoft Graph
              PowerShell for other work.
            </>,
          ]}
        />
        <p className="m-0">
          Guardian&rsquo;s configuration monitors, named <Strong>Guardian Monitor v1</Strong>, v2 and so on, stay in your
          tenant until they&rsquo;re deleted, and count against its Tenant Configuration Management limits until then.
        </p>
        <p className="m-0">
          Guardian keeps the data it has collected about your tenant after you revoke access. Deleting it is covered
          by your agreement with iuvo. Deleted data stays in database backups for up to 35 days and in storage for
          about 60 days. Baseline history in iuvo&rsquo;s Azure DevOps repository has no delete path today.
        </p>
      </DocSection>

      {/* Contact */}
      <DocSection title="Need help?" id="need-help">
        <p className="m-0">
          If you hit an issue during setup, email the Guardian team at{' '}
          <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
            support@iuvotech.com
          </a>
          .
        </p>
      </DocSection>

      {/* Next page */}
      <nav aria-label="Documentation pages" className="mt-12 flex max-w-[56rem] flex-wrap justify-between gap-4 border-t border-line-subtle pt-6">
        <ButtonLink to="/docs" kind="ghost">
          Documentation
        </ButtonLink>
        <ButtonLink to="/docs/permissions" kind="ghost" icon={<IconArrowRight />}>
          Permissions &amp; security
        </ButtonLink>
      </nav>
    </DocsPage>
  )
}
